import React, { useEffect, useState, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, addDays, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { supabase } from '../../lib/supabase';
import StatusBadge from '../../components/StatusBadge';
import FactuurDocument from '../../components/pdf/FactuurDocument';
import HerinneringDocument from '../../components/pdf/HerinneringDocument';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../i18n/nl.js';
import { sendPushToClient } from '../../utils/pushUtils.js';
import { euro, fmtDate } from '../../utils/formatUtils.js';
import {
  generateInvoicePDF,
  uploadInvoicePDF,
  getInvoicePDFBase64,
  sendInvoiceEmail,
  generateInvoiceEmailHTML,
  ensureInvoiceCheckout,
  archiveInvoicePaymentLink,
} from '../../utils/invoiceEmailUtils';
import { blockClient, unblockClient, allCollectionsInvoicesPaid } from '../../utils/blockingUtils.js';

const VOLGENDE_STATUS = {
  draft:           'sent',
  sent:            'paid',
  reminded:        'paid',
  final_notice:    'paid',
  collections:     'paid',
  partially_paid:  'paid',
};
const VOLGENDE_LABEL = {
  draft:          () => t('facturen.markeerVerzonden'),
  sent:           () => t('facturen.markeerBetaald'),
  reminded:       () => t('facturen.markeerBetaald'),
  final_notice:   () => t('facturen.markeerBetaald'),
  collections:    () => t('facturen.markeerBetaaldIncasso'),
  partially_paid: () => t('facturen.volledigBetaaldKnop'),
};

function emptyLine() {
  return { description: '', quantity: '1', unit_price: '', btw_percentage: 9, price_mode: 'excl' };
}

function lineUnitPriceExcl(l) {
  const raw = parseFloat(l.unit_price) || 0;
  if (l.price_mode === 'incl') return raw / (1 + (l.btw_percentage || 0) / 100);
  return raw;
}
function lineExcl(l) { return (parseFloat(l.quantity) || 0) * lineUnitPriceExcl(l); }
function lineBtw(l)  { return Math.round(lineExcl(l) * (l.btw_percentage / 100) * 100) / 100; }
function lineIncl(l) { return lineExcl(l) + lineBtw(l); }

function calcTotals(lines) {
  const subtotal = lines.reduce((s, l) => s + lineExcl(l), 0);
  const btwMap = {};
  lines.forEach(l => { btwMap[l.btw_percentage] = (btwMap[l.btw_percentage] || 0) + lineBtw(l); });
  const totalBtw = Object.values(btwMap).reduce((s, b) => s + b, 0);
  return { subtotal, btwMap, totalBtw, total: subtotal + totalBtw };
}

function daysElapsed(isoDateStr) {
  try { return Math.floor((Date.now() - new Date(isoDateStr).getTime()) / 86400000); }
  catch { return 0; }
}

function reminderTypeLabel(type) {
  if (type === 'reminder')     return t('facturen.histTypeReminder');
  if (type === 'final_notice') return t('facturen.histTypeFinalNotice');
  if (type === 'collections')  return t('facturen.histTypeCollections');
  return type;
}

function ConfirmModal({ title, body, onConfirm, onClose, confirmLabel, danger = false, secondaryAction = null }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ marginBottom: 24, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{body}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>{t('facturen.annuleren')}</button>
          {secondaryAction && (
            <button
              className="btn btn-secondary"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </button>
          )}
          <button
            className="btn btn-primary"
            style={danger ? { background: '#dc2626', borderColor: '#dc2626' } : {}}
            onClick={onConfirm}
          >
            {confirmLabel || t('facturen.bevestigen')}
          </button>
        </div>
      </div>
    </div>
  );
}

function GedeeltelijkModal({ inv, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const totaal = inv.total_amount || 0;
  const ingevoerd = parseFloat(amount) || 0;
  const openstaand = totaal - ingevoerd;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!amount || ingevoerd <= 0 || ingevoerd >= totaal) {
      setError(t('facturen.gedeeltelijkValidatie'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { error: rpcErr } = await supabase.rpc('mark_partially_paid', {
        p_invoice_id: inv.id,
        p_amount_paid: ingevoerd,
      });
      if (rpcErr) throw rpcErr;
      onSaved();
    } catch (err) {
      setError(t('facturen.gedeeltelijkFout') + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('facturen.gedeeltelijkModalTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">{t('facturen.gedeeltelijkTotaal')}</label>
            <div className="form-input" style={{ background: 'var(--color-bg)', cursor: 'default' }}>{euro(totaal)}</div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('facturen.gedeeltelijkBetaaldLabel')}</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('facturen.gedeeltelijkOpenstaand')}</label>
            <div
              className="form-input"
              style={{ background: 'var(--color-bg)', cursor: 'default', color: ingevoerd > 0 && openstaand < 0 ? '#dc2626' : undefined }}
            >
              {ingevoerd > 0 ? euro(openstaand) : '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('facturen.annuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('facturen.gedeeltelijkOpslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PushAanmaningModal({ inv, pushText: initialText, onClose }) {
  const { user } = useAuth();
  const [pushText, setPushText] = useState(initialText);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  async function handleSendPush() {
    if (!pushText.trim()) {
      setError(t('facturen.pushValidatie'));
      return;
    }
    setSending(true);
    setError('');
    try {
      const { success } = await sendPushToClient(
        supabase,
        user.id,
        inv.client_id,
        'billing',
        t('facturen.pushAanmaningTitle'),
        pushText
      );

      if (!success) {
        setError(t('facturen.pushGeenToken'));
        setSending(false);
        return;
      }

      onClose();
    } catch (err) {
      setError(t('facturen.pushFout') + err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('facturen.pushModalTitel').replace('[naam]', inv.client?.full_name || 'cliënt')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
        <div className="form-group">
          <label className="form-label">{t('facturen.pushModalLabel')}</label>
          <textarea
            className="form-input"
            rows={4}
            value={pushText}
            onChange={e => setPushText(e.target.value)}
            style={{ resize: 'vertical' }}
            autoFocus
          />
          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
            {t('facturen.pushModalHint')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={sending}>
            {t('facturen.pushModalOverslaan')}
          </button>
          <button className="btn btn-primary" onClick={handleSendPush} disabled={sending}>
            {sending ? '…' : t('facturen.pushModalVerstuur')}
          </button>
        </div>
      </div>
    </div>
  );
}

function NieuweFactuurModal({ clients, onClose, onCreated, lockedClientId = null, lockedClientName = null }) {
  const [clientId, setClientId] = useState(lockedClientId || '');
  const [periodStart, setPeriodStart] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [periodEnd, setPeriodEnd]     = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [lines, setLines] = useState([emptyLine()]);
  const [memberships, setMemberships] = useState([]);
  const [membershipId, setMembershipId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientId) { setMemberships([]); setMembershipId(''); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('client_memberships')
        .select('id, is_custom, custom_name, membership_types(name)')
        .eq('client_id', clientId)
        .eq('status', 'active');
      if (cancelled) return;
      const list = data || [];
      setMemberships(list);
      setMembershipId(list.length === 1 ? list[0].id : '');
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  function updateLine(idx, field, val) {
    setLines(prev => { const next = [...prev]; next[idx] = { ...next[idx], [field]: val }; return next; });
  }

  const { subtotal, btwMap, totalBtw, total } = calcTotals(lines);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!clientId) { setError(t('facturen.validatieClient')); return; }
    if (lines.some(l => !l.description || !l.unit_price)) { setError(t('facturen.validatieRegels')); return; }
    if (new Date(periodEnd) <= new Date(periodStart)) { setError(t('facturen.validatieDatum')); return; }
    setSaving(true);
    try {
      const { data: invoiceId, error: rpcErr } = await supabase.rpc('create_invoice', {
        p_client_id: clientId,
        p_lines: lines.map(l => ({
          description: l.description,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: lineUnitPriceExcl(l),
          btw_percentage: l.btw_percentage,
        })),
        p_period_start: periodStart,
        p_period_end: periodEnd,
        p_client_membership_id: membershipId || null,
      });
      if (rpcErr) throw rpcErr;
      onCreated(invoiceId);
    } catch (err) {
      setError(t('facturen.foutAanmaken') + err.message);
    } finally {
      setSaving(false);
    }
  }

  const gridCols = '3fr 70px 160px 70px 90px 90px 36px';
  const headerLabels = [
    t('facturen.colOmschrijving'), t('facturen.colAantal'), t('facturen.colStukprijs'),
    t('facturen.colBtwPct'), t('facturen.colExcl'), t('facturen.colIncl'), '',
  ];

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('facturen.modalTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
              <label className="form-label">{t('facturen.labelClient')}</label>
              {lockedClientId ? (
                <div className="form-input" style={{ background: 'var(--color-bg)', color: 'var(--color-text-muted)', cursor: 'default' }}>{lockedClientName}</div>
              ) : (
                <select className="form-input" value={clientId} onChange={e => setClientId(e.target.value)} required>
                  <option value="">{t('facturen.selecteerClient')}</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.full_name || c.email}</option>)}
                </select>
              )}
            </div>
            {clientId && (
              <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                <label className="form-label">{t('facturen.labelAbonnement')}</label>
                <select className="form-input" value={membershipId} onChange={e => setMembershipId(e.target.value)}>
                  <option value="">{t('facturen.geenAbonnementKoppeling')}</option>
                  {memberships.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.is_custom ? m.custom_name : (m.membership_types?.name || 'Abonnement')}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>{t('facturen.abonnementKoppelingHint')}</p>
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('facturen.labelPeriodeStart')}</label>
              <input type="date" className="form-input" value={periodStart} onChange={e => setPeriodStart(e.target.value)} required />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('facturen.labelPeriodeEinde')}</label>
              <input type="date" className="form-input" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} required />
            </div>
          </div>

          <div className="divider" />
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t('facturen.regeloverzicht')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, marginBottom: 8 }}>
            {headerLabels.map((h, i) => (
              <span key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</span>
            ))}
          </div>

          {lines.map((line, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <input className="form-input" placeholder={t('facturen.omschrijvingPlaceholder')} value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)} required />
              <input className="form-input" type="number" min="0.01" step="0.01" placeholder="1" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} required />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <input className="form-input" type="number" step="0.01" placeholder="0,00" value={line.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)} required style={{ flex: 1 }} />
                  <div style={{ display: 'flex', flexDirection: 'row', borderRadius: 20, border: '1px solid var(--color-primary)', overflow: 'hidden', marginLeft: 8 }}>
                    <button type="button" onClick={() => updateLine(idx, 'price_mode', 'excl')} style={{ fontSize: 10, paddingLeft: 8, paddingRight: 8, paddingTop: 4, paddingBottom: 4, cursor: 'pointer', border: 'none', background: line.price_mode !== 'incl' ? 'var(--color-primary)' : '#fff', color: line.price_mode !== 'incl' ? '#fff' : 'var(--color-primary)' }}>{t('facturen.stukprijsExcl')}</button>
                    <button type="button" onClick={() => updateLine(idx, 'price_mode', 'incl')} style={{ fontSize: 10, paddingLeft: 8, paddingRight: 8, paddingTop: 4, paddingBottom: 4, cursor: 'pointer', border: 'none', background: line.price_mode === 'incl' ? 'var(--color-primary)' : '#fff', color: line.price_mode === 'incl' ? '#fff' : 'var(--color-primary)' }}>{t('facturen.stukprijsIncl')}</button>
                  </div>
                </div>
                {line.price_mode === 'incl' && (parseFloat(line.unit_price) || 0) > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{t('facturen.exclRef')} {euro(lineUnitPriceExcl(line))}</div>
                )}
              </div>
              <select className="form-input" value={line.btw_percentage} onChange={e => updateLine(idx, 'btw_percentage', Number(e.target.value))}>
                <option value={9}>{t('facturen.btwOptie9')}</option>
                <option value={21}>{t('facturen.btwOptie21')}</option>
              </select>
              <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{euro(lineExcl(line))}</span>
              <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{euro(lineIncl(line))}</span>
              {lines.length > 1 ? (
                <button type="button" onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', fontSize: 16, cursor: 'pointer' }}>✕</button>
              ) : <span />}
            </div>
          ))}

          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLines(prev => [...prev, emptyLine()])} style={{ marginBottom: 20 }}>
            {t('facturen.regelToevoegen')}
          </button>

          <div className="divider" />
          <div style={{ maxWidth: 300, marginLeft: 'auto', display: 'grid', gap: 8 }}>
            <TotalRow label={t('facturen.subtotaal')} value={euro(subtotal)} />
            {Object.entries(btwMap).sort(([a], [b]) => a - b).map(([pct, amt]) => (
              <TotalRow key={pct} label={`${t('facturen.btw')} ${pct}%`} value={euro(amt)} />
            ))}
            <div style={{ height: 1, background: 'var(--color-border)' }} />
            <TotalRow label={t('facturen.totaalIncl')} value={euro(total)} bold />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('facturen.annuleren')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('facturen.opslaan') : t('facturen.aanmaken')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--color-text-muted)', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontWeight: bold ? 700 : 400, fontSize: bold ? 16 : 14 }}>{value}</span>
    </div>
  );
}

export default function FacturenPage() {
  const { user, role } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoiceLines, setInvoiceLines] = useState({});
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [creditnotaConfirm, setCreditnotaConfirm] = useState(null);
  const [expandedTimelines, setExpandedTimelines] = useState(new Set());
  const [toast, setToast] = useState('');
  const [betaaldConfirm, setBetaaldConfirm] = useState(null);
  const [onbetaaldConfirm, setOnbetaaldConfirm] = useState(null);
  const [gedeeltelijkModal, setGedeeltelijkModal] = useState(null);
  const [volledigBetaaldConfirm, setVolledigBetaaldConfirm] = useState(null);
  const [testmodus, setTestmodus] = useState(false);
  const [pushAanmaningModal, setPushAanmaningModal] = useState(null);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 5000);
  }

  function toggleTimeline(invoiceId) {
    setExpandedTimelines(prev => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [invoicesRes, clientsRes, settingsRes] = await Promise.all([
        supabase
          .from('invoices')
          .select(`
            id, invoice_number, client_id, client_membership_id, subtotal, btw_amount, total_amount, amount_paid,
            status, created_at, period_start, period_end, due_date, emailed_at,
            is_credit_note, credit_note_for,
            client:profiles!invoices_client_id_fkey(id, full_name, email, customer_number, street, house_number, postal_code, city),
            invoice_reminders(type, sent_at, new_due_date)
          `)
          .order('created_at', { ascending: false }),
        supabase
          .from('profiles')
          .select('id, full_name, email, customer_number, street, house_number, postal_code, city')
          .eq('role', 'client')
          .order('full_name'),
        supabase
          .from('settings')
          .select('key, value'),
      ]);

      if (invoicesRes.error) throw invoicesRes.error;
      if (clientsRes.error) throw clientsRes.error;

      const rawInvoices = invoicesRes.data || [];
      const creditNoteForSet = new Set(
        rawInvoices.filter(i => i.credit_note_for).map(i => i.credit_note_for)
      );
      const processed = rawInvoices.map(i => ({
        ...i,
        has_credit_note: creditNoteForSet.has(i.id),
        latest_reminder: i.invoice_reminders?.length
          ? [...i.invoice_reminders].sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))[0]
          : null,
      }));

      setInvoices(processed);
      setClients(clientsRes.data || []);

      const map = {};
      (settingsRes.data || []).forEach(({ key, value }) => { map[key] = value; });
      setCompany(map);
    } catch (err) {
      setError(t('facturen.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function loadLinesForInvoice(invoiceId) {
    if (invoiceLines[invoiceId]) return invoiceLines[invoiceId];
    const { data } = await supabase
      .from('invoice_lines')
      .select('id, description, quantity, unit_price, btw_percentage, amount')
      .eq('invoice_id', invoiceId)
      .order('position');
    const lines = data || [];
    setInvoiceLines(prev => ({ ...prev, [invoiceId]: lines }));
    return lines;
  }

  // Verstuur factuur (draft → sent): F4-2.4 — PDF-upload en statuswijziging
  // in de juiste volgorde met expliciete foutafhandeling, zodat de status
  // nooit op 'sent' staat zonder dat de PDF daadwerkelijk is opgeslagen.
  async function handleVerstuurFactuur(inv) {
    setUpdatingId(inv.id);
    setError('');
    try {
      const lines = await loadLinesForInvoice(inv.id);

      // STAP 1: Genereer + upload de PDF (idempotent: upsert overschrijft).
      const pdfBlob = await generateInvoicePDF(inv, lines, company);
      try {
        await uploadInvoicePDF(inv.id, pdfBlob);
      } catch (uploadErr) {
        // Upload faalde → status NIET op 'sent', stoppen.
        setError(t('facturen.foutUpload') + uploadErr.message);
        return;
      }

      // STAP 2: Pas de status aan naar 'sent' (PDF staat nu vast).
      const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
        p_invoice_id: inv.id,
        p_status: 'sent',
      });
      if (rpcErr) {
        // Statuswijziging faalde → de geüploade PDF blijft staan (idempotent,
        // dezelfde PDF kan opnieuw worden geüpload). Toon een duidelijke fout
        // zodat de coach de actie opnieuw kan proberen.
        setError(t('facturen.foutStatusNaUpload') + rpcErr.message);
        return;
      }

      // STAP 3: Verstuur de e-mail (idempotentie via emailed_at: alleen als
      // de factuur nog niet eerder gemaild is). emailed_at wordt direct na
      // succesvolle verzending gezet.
      if (!inv.emailed_at) {
        const pdfBase64 = await getInvoicePDFBase64(inv.id);

        // F4-10 Stap 3: bij iDEAL en nog geen checkout, checkout aanvragen
        // vlak vóór het versturen van de mail. Falen blokkeert de mail
        // NIET — de factuur wordt dan alsnog zonder betaallink verstuurd.
        //
        // Onderdeel 2/4 (membership-ambiguïteit-fix): eerst de directe FK
        // (invoices.client_membership_id) gebruiken — geen heuristiek meer
        // nodig zodra die gevuld is. Alleen bij oudere/ambigue facturen
        // zonder koppeling valt dit terug op de oude "laatste actieve
        // membership"-heuristiek, MET een zichtbare waarschuwing (niet
        // stil), zodat een ambigue factuur zichtbaar blijft i.p.v.
        // stilzwijgend (mogelijk verkeerd) gekoppeld te worden.
        let activeMembership = null;
        let checkoutUrl = null;
        if (inv.client_membership_id) {
          const { data } = await supabase
            .from('client_memberships')
            .select('id, payment_method, iban_last4')
            .eq('id', inv.client_membership_id)
            .maybeSingle();
          activeMembership = data;
        } else {
          console.warn(
            `Factuur ${inv.invoice_number} (${inv.id}) heeft geen client_membership_id — ` +
            `val terug op de "laatste actieve membership"-heuristiek. Dit kan de VERKEERDE ` +
            `membership opleveren als de client meerdere gelijktijdig actieve memberships heeft.`
          );
          const { data } = await supabase
            .from('client_memberships')
            .select('id, payment_method, iban_last4')
            .eq('client_id', inv.client_id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .order('id', { ascending: false })
            .limit(1)
            .maybeSingle();
          activeMembership = data;
        }

        if (activeMembership?.payment_method === 'ideal') {
          checkoutUrl = await ensureInvoiceCheckout(inv.id);
          if (!checkoutUrl) {
            console.error(`Checkout aanmaken mislukt voor factuur ${inv.id} — mail wordt alsnog zonder betaallink verstuurd.`);
          }
        }

        // F4-10 Stap 4c / SEPA-machtigingsaddendum: bij SEPA geen betaallink,
        // maar een tekstblok dat de aankomende automatische incasso vermeldt
        // (pre-notificatie), inclusief het machtigingskenmerk
        // ("agreement-{agreementId}") — exact dezelfde referentie die bij
        // ondertekenen als mandateReference naar Mollie is gestuurd (zie
        // sign-agreement/index.ts). Opgehaald via signed_agreements.membership_id,
        // want het kenmerk zelf wordt niet apart op client_memberships opgeslagen
        // (daar staat alleen Mollie's eigen mollie_mandate_id).
        let sepaInfo = null;
        if (activeMembership?.payment_method === 'sepa') {
          const { data: agreementForMandate } = await supabase
            .from('signed_agreements')
            .select('id')
            .eq('membership_id', activeMembership.id)
            .order('created_at', { ascending: false })
            .order('id', { ascending: false })
            .limit(1)
            .maybeSingle();
          sepaInfo = {
            ibanLast4: activeMembership.iban_last4,
            mandateReference: agreementForMandate ? `agreement-${agreementForMandate.id}` : null,
          };
        }

        const subject = `Factuur ${inv.invoice_number} — ${company?.company_name || 'Fit En Sterker'}`;
        const htmlBody = generateInvoiceEmailHTML(inv, company, checkoutUrl, sepaInfo);
        const emailResult = await sendInvoiceEmail(inv, subject, htmlBody, pdfBase64, company.email_invoices || company.email_from);

        if (!emailResult.success) {
          // Status is al 'sent' (PDF vast); alleen de e-mail mislukte.
          setError(`Factuur verzonden, maar e-mail mislukt: ${emailResult.error}`);
          setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent' } : i));
          return;
        }

        // Leg het geslaagde verzendmoment vast via dezelfde SECURITY DEFINER-
        // RPC die de coach al toestaat deze factuur te versturen. Een directe
        // tabel-UPDATE viel voor gewone coaches onder de admin-only
        // invoices_manage-policy en raakte daardoor stil 0 rijen.
        const { error: emailedErr } = await supabase.rpc('update_invoice_status', {
          p_invoice_id: inv.id,
          p_status: 'sent',
          p_mark_emailed: true,
        });
        showToast(t('facturen.factuurVerstuurd'));
        if (emailedErr) {
          setError(t('facturen.waarschuwingVerzendstatus'));
          setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent' } : i));
        } else {
          const emailedAt = new Date().toISOString();
          setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent', emailed_at: emailedAt } : i));
        }
      } else {
        // Reeds eerder gemaild → geen tweede e-mail (idempotent).
        showToast(t('facturen.factuurVerstuurd'));
        setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent' } : i));
      }
    } catch (err) {
      setError(t('facturen.foutStatus') + err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  // Download factuur PDF (draft → sent): bevestiging + status wijzigen + PDF downloaden, geen mail
  const [downloadConfirm, setDownloadConfirm] = useState(null);

  async function handleDownloadFactuurPDF(inv, changeStatus = true) {
    setUpdatingId(inv.id);
    setError('');
    try {
      if (changeStatus) {
        // STAP 1: Status wijzigen
        const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
          p_invoice_id: inv.id,
          p_status: 'sent',
        });
        if (rpcErr) throw rpcErr;
      }

      // STAP 2: PDF genereren en downloaden (geen e-mail)
      const lines = await loadLinesForInvoice(inv.id);
      const pdfBlob = await generateInvoicePDF(inv, lines, company);
      if (changeStatus) {
        await uploadInvoicePDF(inv.id, pdfBlob);
      }

      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast(t('facturen.pdfGedownload'));
      if (changeStatus) {
        setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent' } : i));
      }
    } catch (err) {
      setError(t('facturen.foutStatus') + err.message);
    } finally {
      setUpdatingId(null);
      setDownloadConfirm(null);
    }
  }

  // Verstuur herinnering (sent → reminded): status wijzigen + e-mail versturen
  async function handleVerstuurHerinnering(inv) {
    setUpdatingId(inv.id);
    setError('');
    const newDue = addDays(new Date(), 7);
    const newDueStr = format(newDue, 'yyyy-MM-dd');
    try {
      // STAP 1: Registreer herinnering in database
      const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
        p_invoice_id: inv.id,
        p_type: 'reminder',
        p_new_due_date: newDueStr,
      });
      if (rpcErr) throw rpcErr;

      // STAP 2: E-mail versturen met HerinneringDocument
      const { generateReminderEmailHTML, sendInvoiceEmail, ensureInvoicePDF } = await import('../../utils/invoiceEmailUtils');
      const lines = await loadLinesForInvoice(inv.id);
      const subject = `Betalingsherinnering — Factuur ${inv.invoice_number}`;
      const htmlBody = generateReminderEmailHTML(inv, company, 'reminder');
      const pdfBase64 = await ensureInvoicePDF(inv.id, inv, lines, company, { type: 'reminder', newDueDate: newDueStr });
      const emailResult = await sendInvoiceEmail(inv, subject, htmlBody, pdfBase64, company.email_invoices || company.email_from);

      if (!emailResult.success) {
        setError(`Herinnering geregistreerd, maar e-mail mislukt: ${emailResult.error}`);
      } else {
        showToast(t('facturen.herinneringVerstuurd'));
      }

      loadData();
    } catch (err) {
      setError(t('facturen.herinneringFoutLaden') + err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  // Download herinnering PDF (sent → reminded): bevestiging + status wijzigen + PDF downloaden, geen mail
  async function handleDownloadHerinneringPDF(inv, changeStatus = true) {
    setUpdatingId(inv.id);
    setError('');
    const newDue = addDays(new Date(), 7);
    const newDueStr = format(newDue, 'yyyy-MM-dd');
    try {
      if (changeStatus) {
        // STAP 1: Registreer herinnering in database
        const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
          p_invoice_id: inv.id,
          p_type: 'reminder',
          p_new_due_date: newDueStr,
        });
        if (rpcErr) throw rpcErr;
      }

      // STAP 2: HerinneringDocument genereren en downloaden (geen e-mail)
      const lines = await loadLinesForInvoice(inv.id);
      const pdfBlob = await generateInvoicePDF(inv, lines, company, { type: 'reminder', newDueDate: newDueStr });

      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `herinnering-${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast(t('facturen.pdfGedownload'));
      if (changeStatus) {
        loadData();
      }
    } catch (err) {
      setError(t('facturen.herinneringFoutLaden') + err.message);
    } finally {
      setUpdatingId(null);
      setDownloadConfirm(null);
    }
  }

  // Verstuur aanmaning (reminded → final_notice): status wijzigen + e-mail versturen
  async function handleVerstuurAanmaning(inv) {
    setUpdatingId(inv.id);
    setError('');
    const newDue = addDays(new Date(), 14);
    const newDueStr = format(newDue, 'yyyy-MM-dd');
    try {
      // STAP 1: Registreer aanmaning in database
      const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
        p_invoice_id: inv.id,
        p_type: 'final_notice',
        p_new_due_date: newDueStr,
      });
      if (rpcErr) throw rpcErr;

      // STAP 2: E-mail versturen met HerinneringDocument
      const { generateReminderEmailHTML, sendInvoiceEmail, ensureInvoicePDF } = await import('../../utils/invoiceEmailUtils');
      const lines = await loadLinesForInvoice(inv.id);
      const subject = `Laatste aanmaning — Factuur ${inv.invoice_number}`;
      const htmlBody = generateReminderEmailHTML(inv, company, 'final_notice');
      const pdfBase64 = await ensureInvoicePDF(inv.id, inv, lines, company, { type: 'final_notice', newDueDate: newDueStr });
      const emailResult = await sendInvoiceEmail(inv, subject, htmlBody, pdfBase64, company.email_invoices || company.email_from);

      if (!emailResult.success) {
        setError(`Aanmaning geregistreerd, maar e-mail mislukt: ${emailResult.error}`);
        loadData();
        setUpdatingId(null);
        return;
      }

      showToast(t('facturen.aanmaningVerstuurd'));
      loadData();
      setUpdatingId(null);

      // STAP 3: Toon optionele push-dialoog (alleen bij handmatig versturen)
      const { data: settings } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['push_reminder_enabled', 'push_reminder_text']);

      const settingsMap = {};
      (settings || []).forEach(({ key, value }) => { settingsMap[key] = value; });

      if (settingsMap.push_reminder_enabled === 'true') {
        const pushText = settingsMap.push_reminder_text || 'U heeft een openstaande aanmaning. Betaal voor [datum] om incassokosten te voorkomen.';
        const pushTextWithDate = pushText.replace('[datum]', format(newDue, 'd MMMM yyyy', { locale: nl }));
        setPushAanmaningModal({ inv, newDueDate: newDueStr, pushText: pushTextWithDate });
      }
    } catch (err) {
      setError(t('facturen.herinneringFoutLaden') + err.message);
      setUpdatingId(null);
    }
  }

  // Download aanmaning PDF (reminded → final_notice): bevestiging + status wijzigen + PDF downloaden, geen mail
  async function handleDownloadAanmaningPDF(inv, changeStatus = true) {
    setUpdatingId(inv.id);
    setError('');
    const newDue = addDays(new Date(), 14);
    const newDueStr = format(newDue, 'yyyy-MM-dd');
    try {
      if (changeStatus) {
        // STAP 1: Registreer aanmaning in database
        const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
          p_invoice_id: inv.id,
          p_type: 'final_notice',
          p_new_due_date: newDueStr,
        });
        if (rpcErr) throw rpcErr;
      }

      // STAP 2: HerinneringDocument genereren en downloaden (geen e-mail)
      const lines = await loadLinesForInvoice(inv.id);
      const pdfBlob = await generateInvoicePDF(inv, lines, company, { type: 'final_notice', newDueDate: newDueStr });

      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aanmaning-${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast(t('facturen.pdfGedownload'));
      if (changeStatus) {
        loadData();
      }
    } catch (err) {
      setError(t('facturen.herinneringFoutLaden') + err.message);
    } finally {
      setUpdatingId(null);
      setDownloadConfirm(null);
    }
  }

  // Verstuur incasso (final_notice → collections): status wijzigen + e-mail versturen
  async function handleVerstuurIncasso(inv) {
    setUpdatingId(inv.id);
    setError('');
    try {
      // STAP 1: Registreer incasso-overdracht in database
      const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
        p_invoice_id: inv.id,
        p_type: 'collections',
        p_new_due_date: null,
      });
      if (rpcErr) throw rpcErr;

      // STAP 2: E-mail versturen met HerinneringDocument
      const { generateReminderEmailHTML, sendInvoiceEmail, ensureInvoicePDF } = await import('../../utils/invoiceEmailUtils');
      const lines = await loadLinesForInvoice(inv.id);
      const subject = `Incasso-overdracht — Factuur ${inv.invoice_number}`;
      const htmlBody = generateReminderEmailHTML(inv, company, 'collections');
      const pdfBase64 = await ensureInvoicePDF(inv.id, inv, lines, company, { type: 'collections', newDueDate: null });
      const emailResult = await sendInvoiceEmail(inv, subject, htmlBody, pdfBase64, company.email_invoices || company.email_from);

      if (!emailResult.success) {
        setError(`Incasso geregistreerd, maar e-mail mislukt: ${emailResult.error}`);
      } else {
        showToast(t('facturen.incassoVerstuurd'));
      }

      // F4-8: Blokkeer client bij incasso
      try {
        await blockClient(inv.client_id, user.id);
        showToast(t('facturen.blokkeringGeactiveerd'));
      } catch (blockErr) {
        console.error('Blokkering mislukt:', blockErr);
      }

      loadData();
    } catch (err) {
      setError(t('facturen.herinneringFoutLaden') + err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  // Download incasso PDF (final_notice → collections): bevestiging + status wijzigen + PDF downloaden, geen mail
  async function handleDownloadIncassoPDF(inv, changeStatus = true) {
    setUpdatingId(inv.id);
    setError('');
    try {
      if (changeStatus) {
        // STAP 1: Registreer incasso-overdracht in database
        const { error: rpcErr } = await supabase.rpc('send_invoice_reminder', {
          p_invoice_id: inv.id,
          p_type: 'collections',
          p_new_due_date: null,
        });
        if (rpcErr) throw rpcErr;
      }

      // STAP 2: HerinneringDocument genereren en downloaden (geen e-mail)
      const lines = await loadLinesForInvoice(inv.id);
      const pdfBlob = await generateInvoicePDF(inv, lines, company, { type: 'collections', newDueDate: null });

      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `incasso-${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast(t('facturen.pdfGedownload'));
      if (changeStatus) {
        // F4-8: Blokkeer client bij incasso
        try {
          await blockClient(inv.client_id, user.id);
          showToast(t('facturen.blokkeringGeactiveerd'));
        } catch (blockErr) {
          console.error('Blokkering mislukt:', blockErr);
        }
        loadData();
      }
    } catch (err) {
      setError(t('facturen.herinneringFoutLaden') + err.message);
    } finally {
      setUpdatingId(null);
      setDownloadConfirm(null);
    }
  }

  async function handleStatusChange(inv) {
    const nextStatus = VOLGENDE_STATUS[inv.status];
    if (!nextStatus) return;
    setUpdatingId(inv.id);
    setError('');
    try {
      // ═══ STAP 1: Status wijzigen in database ═══
      const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
        p_invoice_id: inv.id,
        p_status: nextStatus,
      });
      if (rpcErr) throw rpcErr;

      // F4-10 kritieke fix, Onderdeel 6: openstaande Payment Link
      // archiveren zodra een factuur handmatig op 'paid' wordt gezet —
      // niet blokkerend, alleen loggen bij falen.
      if (nextStatus === 'paid') {
        archiveInvoicePaymentLink(inv.id, 'manual_paid');
      }

      // F4-8: Deblokkering bij collections → paid
      if (inv.status === 'collections' && nextStatus === 'paid') {
        try {
          const allPaid = await allCollectionsInvoicesPaid(inv.client_id);
          if (allPaid) {
            // Controleer of client geblokkeerd is
            const { data: clientProfile } = await supabase
              .from('profiles')
              .select('blocked_at')
              .eq('id', inv.client_id)
              .maybeSingle();
            if (clientProfile?.blocked_at) {
              await unblockClient(inv.client_id, user.id);
              showToast(t('facturen.blokkeringOpgeheven'));
            }
          }
        } catch (unblockErr) {
          console.error('Deblokkering mislukt:', unblockErr);
        }
      }

      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: nextStatus } : i));
    } catch (err) {
      setError(t('facturen.foutStatus') + err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleMarkeerOnbetaald(inv) {
    setUpdatingId(inv.id);
    setError('');
    try {
      const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
        p_invoice_id: inv.id,
        p_status: 'sent',
      });
      if (rpcErr) throw rpcErr;
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'sent' } : i));
    } catch (err) {
      setError(t('facturen.foutOnbetaald') + err.message);
    } finally {
      setUpdatingId(null);
      setOnbetaaldConfirm(null);
    }
  }

  async function handleVolledigBetaald(inv) {
    setUpdatingId(inv.id);
    setError('');
    try {
      const { error: rpcErr } = await supabase.rpc('update_invoice_status', {
        p_invoice_id: inv.id,
        p_status: 'paid',
      });
      if (rpcErr) throw rpcErr;
      archiveInvoicePaymentLink(inv.id, 'manual_paid');
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'paid' } : i));
    } catch (err) {
      setError(t('facturen.foutStatus') + err.message);
    } finally {
      setUpdatingId(null);
      setVolledigBetaaldConfirm(null);
    }
  }

  async function handleDelete(inv) {
    setActionLoading(inv.id);
    try {
      // Rij-check: DELETE valt onder invoices_manage (admin_coach/superadmin).
      // Een gewone coach ziet deze pagina wél, dus zonder .select('id') zou de
      // factuur uit de lijst verdwijnen terwijl hij in de database blijft staan
      // en bij de eerstvolgende refresh terugkomt.
      const { data: deletedRow, error: delErr } = await supabase
        .from('invoices')
        .delete()
        .eq('id', inv.id)
        .select('id')
        .maybeSingle();
      if (delErr) throw delErr;
      if (!deletedRow) {
        setError(t('facturen.verwijderenGeenRechten'));
        return;
      }
      setInvoices(prev => prev.filter(i => i.id !== inv.id));
      showToast(t('facturen.verwijderenSucces'));
    } catch (err) {
      setError(t('facturen.foutVerwijderen') + err.message);
    } finally {
      setActionLoading(null);
      setDeleteConfirm(null);
    }
  }

  async function handleCreditnota(inv) {
    setActionLoading(inv.id);
    try {
      const { data: creditId, error: rpcErr } = await supabase.rpc('create_credit_note', {
        p_invoice_id: inv.id,
      });
      if (rpcErr) throw rpcErr;
      archiveInvoicePaymentLink(inv.id, 'credit_note');
      let cnNummer = '';
      try {
        const { data: newInv } = await supabase.from('invoices').select('invoice_number').eq('id', creditId).maybeSingle();
        cnNummer = newInv?.invoice_number || '';
      } catch { /* negeer */ }
      showToast(`${t('facturen.creditnotaSucces')} ${cnNummer}`);
      loadData();
    } catch (err) {
      setError(t('facturen.foutCreditnota') + err.message);
    } finally {
      setActionLoading(null);
      setCreditnotaConfirm(null);
    }
  }


  function handleCreated() {
    setShowModal(false);
    loadData();
  }

  const isAdminRole = role === 'superadmin' || role === 'admin_coach';
  const termDays = parseInt(company.company_payment_term_days || '14');

  function isOverdue(inv) {
    if (!inv.created_at) return false;
    try { return addDays(parseISO(inv.created_at), termDays) < new Date(); }
    catch { return false; }
  }

  const invoiceMap = Object.fromEntries(invoices.map(i => [i.id, i.invoice_number]));

  const filtered = invoices.filter(inv => {
    if (filterStatus && inv.status !== filterStatus) return false;
    if (filterClient && inv.client_id !== filterClient) return false;
    return true;
  });

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24,
          background: 'var(--color-primary)', color: '#fff',
          padding: '12px 20px', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999, fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          {toast}
          <button onClick={() => setToast('')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">{t('facturen.title')}</h1>
          <p className="page-subtitle">{invoices.length} {t('facturen.aantalFacturen')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          {t('facturen.nieuweFactuur')}
        </button>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <select className="form-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ flex: '1 1 180px', minWidth: 0, maxWidth: 200 }}>
          <option value="">{t('facturen.alleStatussen')}</option>
          <option value="draft">{t('status.draft')}</option>
          <option value="sent">{t('status.sent')}</option>
          <option value="reminded">{t('status.reminded')}</option>
          <option value="final_notice">{t('status.final_notice')}</option>
          <option value="collections">{t('status.collections')}</option>
          <option value="partially_paid">{t('status.partially_paid')}</option>
          <option value="paid">{t('status.paid')}</option>
        </select>
        <select className="form-input" value={filterClient} onChange={e => setFilterClient(e.target.value)} style={{ flex: '1 1 200px', minWidth: 0, maxWidth: 240 }}>
          <option value="">{t('facturen.alleClients')}</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.full_name || c.email}</option>)}
        </select>
      </div>

      {isAdminRole && (
        <div style={{
          background: '#fef9c3',
          border: '1px solid #fcd34d',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', margin: 0 }}>
            <input
              type="checkbox"
              checked={testmodus}
              onChange={e => setTestmodus(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            {t('facturen.testmodusLabel')}
          </label>
        </div>
      )}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('facturen.colNummer')}</th>
                  <th>{t('facturen.colClient')}</th>
                  <th>{t('facturen.colPeriode')}</th>
                  <th>{t('facturen.colDatum')}</th>
                  <th>{t('facturen.colTotaal')}</th>
                  <th>{t('facturen.colStatus')}</th>
                  <th style={{ textAlign: 'right' }}>{t('facturen.colActies')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        <div style={{ fontSize: 32 }}>🧾</div>
                        <p>{t('facturen.geenFacturen')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map(inv => {
                    const overdueNow = isOverdue(inv);
                    const showReminderBtn = inv.status === 'sent' && !inv.is_credit_note && (testmodus || overdueNow);
                    const showFinalBtn    = inv.status === 'reminded' && !inv.is_credit_note && (testmodus || daysElapsed(inv.latest_reminder?.sent_at) >= 8);
                    const showIncassoBtn  = inv.status === 'final_notice' && !inv.is_credit_note && (testmodus || isAdminRole || daysElapsed(inv.latest_reminder?.sent_at) >= 15);
                    const hasTimeline     = (inv.invoice_reminders?.length ?? 0) > 0;

                    return (
                      <React.Fragment key={inv.id}>
                        <tr>
                          <td className="font-medium">
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              <InvoiceNumberLink
                                inv={{ ...inv, client: inv.client ?? clients.find(c => c.id === inv.client_id) }}
                                onLoadLines={loadLinesForInvoice}
                                lines={invoiceLines[inv.id]}
                                company={company}
                              />
                              {inv.is_credit_note && (
                                <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#fff7ed', color: '#c2410c', fontWeight: 600 }}>
                                  {t('facturen.badgeCreditnota')}
                                </span>
                              )}
                            </div>
                            {inv.is_credit_note && inv.credit_note_for && (
                              <div className="text-sm text-muted">
                                {t('facturen.bijFactuur')} {invoiceMap[inv.credit_note_for] || '—'}
                              </div>
                            )}
                          </td>
                          <td>
                            <div>{inv.client?.full_name || '—'}</div>
                            <div className="text-sm text-muted">{inv.client?.email}</div>
                          </td>
                          <td className="text-muted">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                          <td className="text-muted">{fmtDate(inv.created_at)}</td>
                          <td className="font-semibold">
                            {euro(inv.total_amount)}
                            {inv.status === 'partially_paid' && (
                              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                                {t('facturen.betaaldInfoLabel')} {euro(inv.amount_paid || 0)} — {t('facturen.openstaandInfoLabel')} {euro((inv.total_amount || 0) - (inv.amount_paid || 0))}
                              </div>
                            )}
                          </td>
                          <td><StatusBadge status={inv.status} /></td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>

                              {/* Draft → Sent: Twee knoppen (verstuur + download) */}
                              {inv.status === 'draft' && !inv.is_credit_note && (
                                <>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleVerstuurFactuur(inv)}
                                    disabled={updatingId === inv.id}
                                  >
                                    {updatingId === inv.id ? '…' : t('facturen.verstuurFactuur')}
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setDownloadConfirm({ inv, type: 'draft', targetStatus: 'sent' })}
                                    disabled={updatingId === inv.id}
                                  >
                                    {t('facturen.downloadPdf')}
                                  </button>
                                </>
                              )}

                              {/* Sent zonder emailed_at: de factuur is al 'sent' (bv. direct
                                  bij ondertekenen aangemaakt, zie sign-agreement) maar de
                                  automatische factuurmail is toen mislukt — bied dezelfde
                                  "Verstuur factuur"-knop als retry aan (handleVerstuurFactuur
                                  is idempotent op emailed_at). */}
                              {inv.status === 'sent' && !inv.emailed_at && !inv.is_credit_note && (
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => handleVerstuurFactuur(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {updatingId === inv.id ? '…' : t('facturen.verstuurFactuur')}
                                </button>
                              )}

                              {/* Sent → Paid: Markeer betaald knop (reguliere flow) */}
                              {inv.status === 'sent' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.markeerBetaald')}
                                </button>
                              )}

                              {/* Reminded → Paid: Markeer betaald knop */}
                              {inv.status === 'reminded' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.markeerBetaald')}
                                </button>
                              )}

                              {/* Final_notice → Paid: Markeer betaald knop */}
                              {inv.status === 'final_notice' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.markeerBetaald')}
                                </button>
                              )}

                              {/* Collections → Paid: Markeer betaald knop */}
                              {inv.status === 'collections' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.markeerBetaaldIncasso')}
                                </button>
                              )}

                              {/* Markeer betaald voor creditnota's (5C) */}
                              {inv.is_credit_note && inv.status === 'sent' && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.markeerBetaald')}
                                </button>
                              )}

                              {/* Markeer onbetaald (5B) */}
                              {inv.status === 'paid' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  disabled={updatingId === inv.id}
                                  onClick={() => setOnbetaaldConfirm(inv)}
                                >
                                  {t('facturen.onbetaaldKnop')}
                                </button>
                              )}


                              {/* Sent → Reminded: Twee knoppen (verstuur + download) */}
                              {showReminderBtn && (
                                <>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleVerstuurHerinnering({ ...inv, client: inv.client ?? clients.find(c => c.id === inv.client_id) })}
                                    disabled={updatingId === inv.id}
                                  >
                                    {updatingId === inv.id ? '…' : t('facturen.verstuurHerinnering')}
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setDownloadConfirm({ inv: { ...inv, client: inv.client ?? clients.find(c => c.id === inv.client_id) }, type: 'reminder', targetStatus: 'reminded' })}
                                    disabled={updatingId === inv.id}
                                  >
                                    {t('facturen.downloadPdf')}
                                  </button>
                                </>
                              )}

                              {/* Reminded → Final_notice: Twee knoppen (verstuur + download) */}
                              {showFinalBtn && (
                                <>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    style={{ background: '#ea580c', borderColor: '#ea580c' }}
                                    onClick={() => handleVerstuurAanmaning({ ...inv, client: inv.client ?? clients.find(c => c.id === inv.client_id) })}
                                    disabled={updatingId === inv.id}
                                  >
                                    {updatingId === inv.id ? '…' : t('facturen.verstuurAanmaning')}
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setDownloadConfirm({ inv: { ...inv, client: inv.client ?? clients.find(c => c.id === inv.client_id) }, type: 'final_notice', targetStatus: 'final_notice' })}
                                    disabled={updatingId === inv.id}
                                  >
                                    {t('facturen.downloadPdf')}
                                  </button>
                                </>
                              )}

                              {/* Final_notice → Collections: Twee knoppen (verstuur + download) */}
                              {showIncassoBtn && (
                                <>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    style={{ background: '#dc2626', borderColor: '#dc2626' }}
                                    onClick={() => handleVerstuurIncasso({ ...inv, client: inv.client ?? clients.find(c => c.id === inv.client_id) })}
                                    disabled={updatingId === inv.id}
                                  >
                                    {updatingId === inv.id ? '…' : t('facturen.verstuurIncasso')}
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setDownloadConfirm({ inv: { ...inv, client: inv.client ?? clients.find(c => c.id === inv.client_id) }, type: 'collections', targetStatus: 'collections' })}
                                    disabled={updatingId === inv.id}
                                  >
                                    {t('facturen.downloadPdf')}
                                  </button>
                                </>
                              )}

                              {/* Gedeeltelijk betaald (6) */}
                              {['sent', 'reminded', 'final_notice', 'collections'].includes(inv.status) && !inv.is_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: '#92400e', borderColor: '#92400e' }}
                                  onClick={() => setGedeeltelijkModal(inv)}
                                >
                                  {t('facturen.gedeeltelijkBetaald')}
                                </button>
                              )}

                              {/* Volledig betaald bij partially_paid (6) */}
                              {inv.status === 'partially_paid' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setVolledigBetaaldConfirm(inv)}
                                  disabled={updatingId === inv.id}
                                >
                                  {t('facturen.volledigBetaaldKnop')}
                                </button>
                              )}

                              {/* Creditnota */}
                              {(inv.status === 'sent' || inv.status === 'paid') && !inv.is_credit_note && !inv.has_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: '#ea580c', borderColor: '#ea580c' }}
                                  disabled={actionLoading === inv.id}
                                  onClick={() => setCreditnotaConfirm(inv)}
                                >
                                  {t('facturen.creditnota')}
                                </button>
                              )}

                              {/* Verwijderen — alleen concept */}
                              {inv.status === 'draft' && !inv.is_credit_note && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: '#dc2626', borderColor: '#dc2626' }}
                                  disabled={actionLoading === inv.id}
                                  onClick={() => setDeleteConfirm(inv)}
                                >
                                  {t('facturen.verwijderen')}
                                </button>
                              )}

                              {/* Tijdlijn toggle */}
                              {hasTimeline && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => toggleTimeline(inv.id)}
                                  title={t('facturen.herinneringGeschieden')}
                                >
                                  {expandedTimelines.has(inv.id) ? '▲' : '▼'} {t('facturen.herinneringGeschieden')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Tijdlijn rij */}
                        {expandedTimelines.has(inv.id) && hasTimeline && (
                          <tr style={{ background: 'var(--color-bg)' }}>
                            <td colSpan={7} style={{ paddingTop: 8, paddingBottom: 14, paddingLeft: 24 }}>
                              <div style={{ borderLeft: '3px solid var(--color-border)', paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[...inv.invoice_reminders]
                                  .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at))
                                  .map((r, i) => (
                                    <div key={i} style={{ fontSize: 12 }}>
                                      <span style={{ color: 'var(--color-text-muted)', marginRight: 8 }}>{fmtDate(r.sent_at)}</span>
                                      <span style={{ fontWeight: 600 }}>{reminderTypeLabel(r.type)}</span>
                                      {r.new_due_date && (
                                        <span style={{ marginLeft: 8, color: 'var(--color-text-muted)' }}>
                                          {t('facturen.histNieuweBetaaldatum')} {fmtDate(r.new_due_date)}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <NieuweFactuurModal clients={clients} onClose={() => setShowModal(false)} onCreated={handleCreated} />
      )}

      {deleteConfirm && (
        <ConfirmModal
          title={t('facturen.verwijderenTitel')}
          body={t('facturen.verwijderenTekst')}
          confirmLabel={t('facturen.verwijderen')}
          danger
          onConfirm={() => handleDelete(deleteConfirm)}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      {creditnotaConfirm && (
        <ConfirmModal
          title={t('facturen.creditnotaTitel')}
          body={`${t('facturen.creditnotaTekstPrefix')} ${creditnotaConfirm.invoice_number}${t('facturen.creditnotaTekstSuffix')}`}
          confirmLabel={t('facturen.creditnota')}
          onConfirm={() => handleCreditnota(creditnotaConfirm)}
          onClose={() => setCreditnotaConfirm(null)}
        />
      )}


      {/* Bevestiging betaald (5A + 5C + incasso) */}
      {betaaldConfirm && (
        <ConfirmModal
          title={t('facturen.betaaldBevestigenTitel')}
          body={betaaldConfirm.status === 'collections'
            ? t('facturen.collectionsNaarBetaaldTekst')
            : `Weet je zeker dat je factuur ${betaaldConfirm.invoice_number} als betaald wilt markeren? Dit kan ongedaan worden gemaakt via 'Markeer onbetaald'.`}
          confirmLabel={t('facturen.betaaldBevestigenKnop')}
          onConfirm={() => { handleStatusChange(betaaldConfirm); setBetaaldConfirm(null); }}
          onClose={() => setBetaaldConfirm(null)}
        />
      )}

      {/* Bevestiging onbetaald (5B) */}
      {onbetaaldConfirm && (
        <ConfirmModal
          title={t('facturen.onbetaaldTitel')}
          body={`Weet je zeker dat je factuur ${onbetaaldConfirm.invoice_number} terug wilt zetten naar Verzonden? Gebruik dit alleen bij een vergissing.`}
          confirmLabel={t('facturen.onbetaaldKnopBevestigen')}
          onConfirm={() => handleMarkeerOnbetaald(onbetaaldConfirm)}
          onClose={() => setOnbetaaldConfirm(null)}
        />
      )}

      {/* Volledig betaald bevestiging (6) */}
      {volledigBetaaldConfirm && (
        <ConfirmModal
          title={t('facturen.volledigBetaaldTitel')}
          body={t('facturen.volledigBetaaldTekst')}
          confirmLabel={t('facturen.volledigBetaaldBevestigen')}
          onConfirm={() => handleVolledigBetaald(volledigBetaaldConfirm)}
          onClose={() => setVolledigBetaaldConfirm(null)}
        />
      )}

      {/* Gedeeltelijk betaald modal (6) */}
      {gedeeltelijkModal && (
        <GedeeltelijkModal
          inv={gedeeltelijkModal}
          onClose={() => setGedeeltelijkModal(null)}
          onSaved={() => { setGedeeltelijkModal(null); loadData(); }}
        />
      )}

      {/* Download PDF bevestiging (ONDERDEEL 2) */}
      {downloadConfirm && (
        <ConfirmModal
          title={t('facturen.downloadBevestigenTitel')}
          body={t('facturen.downloadBevestigenTekst').replace('{status}', t('facturen.downloadBevestigenStatus.' + downloadConfirm.type))}
          confirmLabel={t('facturen.downloadBevestigenJa')}
          onConfirm={() => {
            if (downloadConfirm.type === 'draft') {
              handleDownloadFactuurPDF(downloadConfirm.inv, true);
            } else if (downloadConfirm.type === 'reminder') {
              handleDownloadHerinneringPDF(downloadConfirm.inv, true);
            } else if (downloadConfirm.type === 'final_notice') {
              handleDownloadAanmaningPDF(downloadConfirm.inv, true);
            } else if (downloadConfirm.type === 'collections') {
              handleDownloadIncassoPDF(downloadConfirm.inv, true);
            }
          }}
          onClose={() => setDownloadConfirm(null)}
          secondaryAction={{
            label: t('facturen.downloadBevestigenAlleenPdf'),
            onClick: () => {
              if (downloadConfirm.type === 'draft') {
                handleDownloadFactuurPDF(downloadConfirm.inv, false);
              } else if (downloadConfirm.type === 'reminder') {
                handleDownloadHerinneringPDF(downloadConfirm.inv, false);
              } else if (downloadConfirm.type === 'final_notice') {
                handleDownloadAanmaningPDF(downloadConfirm.inv, false);
              } else if (downloadConfirm.type === 'collections') {
                handleDownloadIncassoPDF(downloadConfirm.inv, false);
              }
            },
          }}
        />
      )}

      {/* Push notificatie na aanmaning (optioneel) */}
      {pushAanmaningModal && (
        <PushAanmaningModal
          inv={pushAanmaningModal.inv}
          pushText={pushAanmaningModal.pushText}
          onClose={() => setPushAanmaningModal(null)}
        />
      )}
    </div>
  );
}

function InvoiceNumberLink({ inv, onLoadLines, lines, company }) {
  const [loading, setLoading] = useState(false);

  async function handleClick(e) {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      // Laad regels als die nog niet geladen zijn
      const invoiceLines = lines || await onLoadLines(inv.id);

      // Genereer PDF blob
      const pdfBlob = await generateInvoicePDF(inv, invoiceLines, company);

      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{
        color: loading ? 'var(--color-text-muted)' : 'var(--color-primary)',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        cursor: loading ? 'wait' : 'pointer',
        fontWeight: 600,
      }}
      title="Klik om factuur-PDF te downloaden"
    >
      {loading ? '…' : inv.invoice_number}
    </a>
  );
}
